import { plainText, richText } from '../../../../modules/validators';
import { trimSpaces, trimSpacesLeft } from '../../../../modules/formatters';
import { FormVmItemConfigType, FormVmValueValidatorResultType, InitialData } from '../../../../infrastructure/form-viewmodel-controller';
import { pagePathSymbolsValidator, reservedPathValidator } from '../../../../modules/passport-pages/config';
import { DESCRIPTION as DESCRIPTION_LABEL, EXTERNAL_ID_TEXT, TYPE as TYPE_LABEL } from '../../../../constants/common-labels.constants';
import { buildConfigList } from '../../../../infrastructure/utils/form-config.utils';

export enum FieldKey {
    NAME = 'name',
    DESCRIPTION = 'description',
    MEETING_STATUS = 'status',
    MEETING_ACCESS = 'meetingAccess',
    REPLAY = 'replayEnabled',
    FEATURED = 'featured',
    TYPE = 'type',
    TRACK = 'track',
    TOPIC = 'topic',
    NOTES = 'notes',
    APPLY_CONNECTIONS_TEMPLATE = 'applyConnectionsTemplate',
    CONNECTIONS_TEMPLATE = 'connectionsBlueprintId',
    SINGLE_MEETING_CUSTOM_PAGE = 'singleMeetingCustomPage',
    PAGE_LABEL = 'label',
    PAGE_PATH = 'path',
    LINK_DESCRIPTION = 'linkDescription',
    PAGE_LINK_ENABLED = 'pageLinkEnabled',
    LANDING_PAGE = 'landingPage',
    DISABLE_OTHER_PAGES = 'disableOtherPages',
    CONNECTION_VERSION = 'connectionVersion',
    RECORD = 'recordEnabled',
    EXPIRY_DAYS = 'expiryDays',
    EXTERNAL_ID = 'externalId',
}

export const requiredWithSingleMeetingCustomPage = (val: string, formValues: InitialData): FormVmValueValidatorResultType => {
    return formValues[FieldKey.SINGLE_MEETING_CUSTOM_PAGE] ? plainText.required(val) : { valid: true, validationMessage: null };
};

export const validatePagePathSymbolWithSingleMeetingCustomPage = (val: string, formValues: InitialData): FormVmValueValidatorResultType => {
    return formValues[FieldKey.SINGLE_MEETING_CUSTOM_PAGE] ? pagePathSymbolsValidator(val) : { valid: true, validationMessage: null };
};

export const connectionsTemplateValidator = (val: string, formValues: InitialData): FormVmValueValidatorResultType => {
    const valid = formValues[FieldKey.APPLY_CONNECTIONS_TEMPLATE] ? !!val : true;
    const validationMessage = valid
        ? null
        : { id: 'oe.validation.connectionsTemplateRequired.message', defaultMessage: 'You must select connection template.' };

    return { valid: valid, validationMessage: validationMessage };
};

export const FORM_CONFIG = {
    [FieldKey.NAME]: {
        labelDescriptor: { id: 'oe.templates.meeting.name', defaultMessage: 'Name', description: 'Label text' },
        defaultValue: '',
        validators: [plainText.required, plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.DESCRIPTION]: {
        labelDescriptor: DESCRIPTION_LABEL,
        defaultValue: '',
        validators: [plainText.maxLength.long],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.MEETING_STATUS]: {
        labelDescriptor: { id: 'oe.templates.meeting.status', defaultMessage: 'Meeting Status', description: 'Label text' },
        defaultValue: '',
    },
    [FieldKey.MEETING_ACCESS]: {
        labelDescriptor: { id: 'oe.templates.meeting.access', defaultMessage: 'Meeting Access', description: 'Label text' },
        defaultValue: '',
    },
    [FieldKey.REPLAY]: {
        defaultValue: false
    },
    [FieldKey.FEATURED]: {
        defaultValue: false,
    },
    [FieldKey.TYPE]: {
        labelDescriptor: TYPE_LABEL,
        defaultValue: '',
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.TRACK]: {
        labelDescriptor: { id: 'oe.templates.meeting.track', defaultMessage: 'Track', description: 'Label text' },
        defaultValue: '',
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.TOPIC]: {
        labelDescriptor: { id: 'oe.templates.meeting.topic', defaultMessage: 'Topic', description: 'Label text' },
        defaultValue: '',
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.NOTES]: {
        labelDescriptor: { id: 'oe.templates.meeting.notes', defaultMessage: 'Meeting Notes (for OE Passport)', description: 'Label text' },
        defaultValue: '',
        validators: [richText.maxLength.short],
    },
    [FieldKey.APPLY_CONNECTIONS_TEMPLATE]: {
        defaultValue: false
    },
    [FieldKey.CONNECTIONS_TEMPLATE]: {
        labelDescriptor: { id: 'oe.blueprints.meeting.connectionsTemplate', defaultMessage: 'Connection Template', description: 'Label text' },
        validators: [connectionsTemplateValidator],
        defaultValue: ''
    },
    [FieldKey.SINGLE_MEETING_CUSTOM_PAGE]: {
        defaultValue: false
    },
    [FieldKey.PAGE_PATH]: {
        labelDescriptor: { id: `oe.input.label.pagePath`, defaultMessage: 'Page Path' },
        defaultValue: '',
        validators: [
            requiredWithSingleMeetingCustomPage,
            plainText.maxLength.short,
            validatePagePathSymbolWithSingleMeetingCustomPage,
            reservedPathValidator
        ],
        formatters: [trimSpaces]
    },
    [FieldKey.PAGE_LABEL]: {
        labelDescriptor: { id: `oe.input.label.pageLabel`, defaultMessage: 'Page Label' },
        defaultValue: '',
        validators: [requiredWithSingleMeetingCustomPage, plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.PAGE_LINK_ENABLED]: {
        defaultValue: false
    },
    [FieldKey.LINK_DESCRIPTION]: {
        labelDescriptor: { id: `oe.input.label.linkDescription`, defaultMessage: '{prefix}Description' },
        defaultValue: '',
        validators: [plainText.maxLength.medium],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.LANDING_PAGE]: {
        defaultValue: false
    },
    [FieldKey.DISABLE_OTHER_PAGES]: {
        defaultValue: false
    },
    [FieldKey.CONNECTION_VERSION]: {
        defaultValue: true
    },
    [FieldKey.RECORD]: {
        defaultValue: false
    },
    [FieldKey.EXPIRY_DAYS]: {
        defaultValue: null
    },
    [FieldKey.EXTERNAL_ID]: {
        labelDescriptor: EXTERNAL_ID_TEXT,
        defaultValue: '',
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
};

export const configList: FormVmItemConfigType[] = buildConfigList(FORM_CONFIG);
